import {
  AccentIcon,
  AccentIconShapeEnum,
  AccentIconSizeEnum,
  AccentVariantEnum,
} from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { IconText } from '@delmonicos/shared-design-system-react/src/components/icon-text/icon-text';
import {
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { isSocketActive } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/connector-status.helper';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { getGenericPowerType } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/power-type.helper';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  ConnectorFormat,
  PowerType,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import * as globalCss from '@shared/styles/global.module.scss';

import { ChargePointChannelPhysicalSocketDeleteController } from '../charge-point-channel-physical-socket-delete/charge-point-channel-physical-socket-delete.controller';
import { ChargePointChannelPhysicalSocketUpdateController } from '../charge-point-channel-physical-socket-update/charge-point-channel-physical-socket-update.controller';
import {
  useChannelEditModeContext,
  useChargePointChannelContext,
  useChargePointChannelPhysicalSocketContext,
} from '../charge-point.context';
import { mapStatusToGenericConnectorStatus } from '../helper/connector-status.helper';
import { mapTypeToGenericConnectorType } from '../helper/connector-type.helper';
import * as css from './charge-point-channel-physical-socket-details.module.scss';

interface ChargePointChannelPhysicalSocketDetailsControllerProps {
  handleUnlockPhysicalSocket: () => void;
}

export const ChargePointChannelPhysicalSocketDetailsController = ({
  handleUnlockPhysicalSocket,
}: ChargePointChannelPhysicalSocketDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const physicalSocket = useChargePointChannelPhysicalSocketContext();
  const { isEditMode } = useChannelEditModeContext();
  const channel = useChargePointChannelContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canUpdatePhysicalSocket = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.PHYSICAL_SOCKETS,
        action: ActionsByResource.PHYSICAL_SOCKETS.UPDATE,
      }),
    [userPermissions],
  );

  const canDeletePhysicalSocket = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.PHYSICAL_SOCKETS,
        action: ActionsByResource.PHYSICAL_SOCKETS.DELETE,
      }),
    [userPermissions],
  );

  // ACTIONS: UPDATE RESOURCE //////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // ACTIONS: DELETE RESOURCE //////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // CONNECTOR /////////////////////////////////////////////////////////////////

  const isDeleteDisabled = useMemo(
    () =>
      isSocketActive(physicalSocket.ocppStatus) ||
      (channel.physicalSockets?.some((socket) =>
        isSocketActive(socket.ocppStatus),
      ) ??
        false),
    [channel.physicalSockets, physicalSocket.ocppStatus],
  );

  const powerType = getGenericPowerType(physicalSocket.powerType);
  const connectorType = mapTypeToGenericConnectorType(physicalSocket.standard);
  const status = mapStatusToGenericConnectorStatus(physicalSocket.ocppStatus);

  const physicalSocketRealVoltage =
    physicalSocket.powerType === PowerType.AC_3_PHASE
      ? computeTreePhaseVoltage(
          Number.parseInt(`${physicalSocket.voltage}`, 10),
        )
      : physicalSocket.voltage;

  const physicalSocketPower =
    physicalSocket.powerType &&
    physicalSocketRealVoltage &&
    physicalSocket.amperage
      ? ((physicalSocket.amperage * physicalSocketRealVoltage) / 1000).toFixed(
          0,
        )
      : '?';

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Card gap={GapEnum.S} alignItems="stretch" direction="row">
        <Card.FullHeight
          className={classNames(
            css.physicalSocketStatus,
            css[status.accentVariant],
          )}>
          <Text fontWeight={TextWeight.Medium} fontSize={TextFontSize.H3}>
            {physicalSocket.ocppConnectorId}
          </Text>
        </Card.FullHeight>
        <Stack gap={GapEnum.S} flex={1} alignItems="stretch" direction="column">
          <Stack flex={1} justifyContent="space-between">
            <AccentTitle
              icon={connectorType.icon}
              iconShape={AccentIconShapeEnum.Round}
              variant={status.accentVariant}
              text={t(
                `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type-alt.${connectorType.name}`,
              )}
              as={HeadingEnum.H2}
            />

            {!isEditMode && (
              <ButtonIcon
                icon="lock-unlocked-01"
                onClick={handleUnlockPhysicalSocket}
              />
            )}
          </Stack>
          <Stack alignItems="center" flex={1} justifyContent="space-between">
            <IconText
              iconName={
                powerType ? `power-${powerType.toLowerCase()}` : `lightning-02`
              }
              iconProps={{
                color: IconColor.Secondary,
                iconSize: IconSize.Small,
              }}
              text={
                powerType
                  ? t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.power-type-alt.${powerType}`,
                    )
                  : '-'
              }
              textProps={{
                fontWeight: TextWeight.Regular,
                fontSize: TextFontSize.Medium,
                color: TextColor.Secondary,
              }}
            />
            <IconText
              iconName="lightning-02"
              iconProps={{
                color: IconColor.Secondary,
                iconSize: IconSize.Small,
              }}
              text={`${physicalSocketPower} kW`}
              textProps={{
                fontWeight: TextWeight.Regular,
                fontSize: TextFontSize.Medium,
                color: TextColor.Secondary,
              }}
            />
            <IconText
              iconName="lightning-01"
              iconProps={{
                color: IconColor.Secondary,
                iconSize: IconSize.Small,
              }}
              text={`${physicalSocket.amperage} A`}
              textProps={{
                fontWeight: TextWeight.Regular,
                fontSize: TextFontSize.Medium,
                color: TextColor.Secondary,
              }}
            />
            <AccentIcon
              shape={AccentIconShapeEnum.Round}
              variant={AccentVariantEnum.Surface}
              size={AccentIconSizeEnum.ExtraSmall}
              icon={
                physicalSocket?.format === ConnectorFormat.CABLE
                  ? 'wire'
                  : 'no-wire'
              }
            />
          </Stack>
          <Stack
            alignItems="center"
            flex={1}
            gap={GapEnum.M}
            className={globalCss.flexedItems}>
            {isEditMode && canUpdatePhysicalSocket && (
              <ButtonIcon
                icon="edit-05"
                onClick={() => setActionUpdateIsDialogOpen(true)}
              />
            )}
            {isEditMode && canDeletePhysicalSocket && (
              <ButtonIcon
                icon="trash-01"
                onClick={() => setActionDeleteIsDialogOpen(true)}
                color={ButtonColor.Destructive}
                state={
                  isDeleteDisabled ? ButtonState.Disabled : ButtonState.Default
                }
                disabled={isDeleteDisabled}
              />
            )}
          </Stack>
        </Stack>
      </Card>
      <Dialog
        open={actionUpdateIsDialogOpen}
        onClose={() => setActionUpdateIsDialogOpen(false)}
        confirmClose>
        <Stack direction="column" gap={GapEnum.M}>
          <Dialog.Header
            onClose={() => setActionUpdateIsDialogOpen(false)}
            title={t('$.charge-point-channel-physical-socket-update.title', {
              number: physicalSocket.ocppConnectorId,
            })}
            icon="wire"
          />
          <Dialog.Content>
            <ChargePointChannelPhysicalSocketUpdateController
              onCancel={() => setActionUpdateIsDialogOpen(false)}
              onSuccess={() => setActionUpdateIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Stack>
      </Dialog>
      <Dialog
        open={actionDeleteIsDialogOpen}
        onClose={() => setActionDeleteIsDialogOpen(false)}
        confirmClose>
        <Stack direction="column" gap={GapEnum.M}>
          <Dialog.Header
            onClose={() => setActionDeleteIsDialogOpen(false)}
            title={t('$.charge-point-channel-physical-socket-delete.title', {
              number: physicalSocket.ocppConnectorId,
            })}
            icon="wire"
          />
          <Dialog.Content>
            <ChargePointChannelPhysicalSocketDeleteController
              onCancel={() => setActionDeleteIsDialogOpen(false)}
              onSuccess={() => setActionDeleteIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Stack>
      </Dialog>
    </>
  );
};
